// API auth fetch — global fetch interceptor that sends /api/* calls to sm4rt-api
// with an Entra bearer token.
//
// Every service calls relative `/api/...` URLs. In dev the CRA proxy forwards those
// to sm4rt-api on :7071 (which has no Easy Auth). In production the site is a Static
// Web App and sm4rt-api is a different origin behind Easy Auth, so each call needs:
//   1. the API's address in front of the path (REACT_APP_API_BASE_URL), and
//   2. `Authorization: Bearer <token for api://sm4rt-api/access_as_user>`.
//
// Doing that here, once, rather than at ~340 call sites is deliberate: a call site
// that missed the change would 401 in production only. The same mechanism as
// apiCallLogger (replace window.fetch), and like it, nothing outside /api/* is touched
// — Graph, blob SAS downloads and other absolute URLs pass through unchanged.
//
// Installed from App.tsx at module scope, i.e. before index.tsx's first fetch.

import {
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { apiRequest } from '../authConfig';

let msal: IPublicClientApplication | null = null;
let originalFetch: typeof window.fetch | null = null;
let redirecting = false;

/** The API origin, without a trailing slash. Empty in dev: the CRA proxy serves /api. */
export function apiBaseUrl(): string {
  return (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
}

/** Prefix an /api path with the API origin. Leaves the path relative in dev. */
export function apiUrl(path: string): string {
  return `${apiBaseUrl()}${path}`;
}

function isApiPath(pathname: string): boolean {
  return pathname === '/api' || pathname.startsWith('/api/');
}

// jsdom (the test environment) has no Request; every browser does.
function isRequest(input: unknown): input is Request {
  return typeof Request !== 'undefined' && input instanceof Request;
}

/**
 * The `/api/...` path (+ query) a fetch input addresses, or null when it is not an
 * sm4rt-api call. Relative paths and same-origin absolute URLs both count: the
 * SignalR client and `new Request('/api/x')` resolve against the page origin.
 */
export function apiPathOf(input: RequestInfo | URL): string | null {
  let raw: string;
  if (typeof input === 'string') raw = input;
  else if (input instanceof URL) raw = input.href;
  else if (isRequest(input)) raw = input.url;
  else return null;

  if (raw.startsWith('/')) {
    const path = raw.split(/[?#]/)[0];
    return isApiPath(path) ? raw : null;
  }
  try {
    const parsed = new URL(raw);
    if (parsed.origin !== window.location.origin) return null;
    return isApiPath(parsed.pathname) ? parsed.pathname + parsed.search : null;
  } catch {
    return null;
  }
}

/**
 * An access token for sm4rt-api, or null when nobody is signed in yet (the request
 * then goes out without one and the API answers 401, which callers already handle).
 * When the token can only be renewed interactively, starts one sign-in redirect and
 * rejects — every other in-flight call rejects with it rather than racing a second.
 */
export async function getApiAccessToken(): Promise<string | null> {
  if (!msal) return null;
  await msal.initialize(); // idempotent; MsalProvider calls it too
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msal.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError && !redirecting) {
      redirecting = true;
      await msal.acquireTokenRedirect({ ...apiRequest, account });
    }
    throw e;
  }
}

/** Replace window.fetch so /api/* calls reach sm4rt-api with a bearer token. */
export function installApiAuthFetch(instance: IPublicClientApplication): void {
  msal = instance;
  // No fetch to wrap under jsdom, where App.test imports App (and so this module).
  if (originalFetch || typeof window.fetch !== 'function') return;
  originalFetch = window.fetch.bind(window);
  const passthrough = originalFetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const path = apiPathOf(input);
    if (path === null) return passthrough(input, init);

    const headers = new Headers(
      init?.headers ?? (isRequest(input) ? input.headers : undefined)
    );
    if (!headers.has('Authorization')) {
      const token = await getApiAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    const target = apiUrl(path);
    if (isRequest(input)) {
      return passthrough(new Request(target, input), { ...init, headers });
    }
    return passthrough(target, { ...init, headers });
  };
}

/** Restore the original fetch. For tests. */
export function uninstallApiAuthFetch(): void {
  if (originalFetch) window.fetch = originalFetch;
  originalFetch = null;
  msal = null;
  redirecting = false;
}
